import {
  transformedGatewaysWithMachinesListEntryTypes,
  MachinesListDataTypes,
} from "@common/types/machines-list-types";
import { getLogger } from "@src/cloud-connect/dependency-injection-container/get-logger";
import {
  errorSnackbar,
  successSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { updateSourceName } from "@src/cloud-connect/use-cases/update-source-name";
import { useI18N } from "@src/language/language-context";

import { RootState } from "@src/reducers/store";
import { updateAllMachinesListData } from "@src/reducers/your-reducer";
import { ChangeEvent, useEffect, useState } from "react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";

export interface UseEditSourceNameMachinesListHookPropsTypes {
  showInput: boolean;
  editSourceName: (e) => void;
  onKeyDownFunc: (e) => void;
  updateNewSourceName: (e, value: string) => void;
  saveSourceName: (e) => void;
  cancelSaveSourceName: (e) => void;
  newSourceName: string;
}

export const updateSourceNameInMachinesList = (
  sourcesMap: Record<string, MachinesListDataTypes>,
  sourceId: string,
  newName: string,
): boolean => {
  const source = sourcesMap[sourceId];
  if (source && source.name !== newName) {
    source.name = newName;
    return true;
  }
  return false;
};

export const useEditSourceNameMachinesListHook = (
  source: MachinesListDataTypes,
): UseEditSourceNameMachinesListHookPropsTypes => {
  const dispatch = useDispatch();
  const i18n = useI18N();
  const previousName = source.name;

  const allMachinesListData = useSelector(
    (state: RootState) => state.yourSlice.allMachinesListData,
    shallowEqual,
  );

  const texts = {
    errorInputEmpty: i18n("modules.editSourceName.errorInputEmpty"),
    errorUpdateFailed: i18n(
      "modules.editSourceName.errorUpdateFailed",
    ),
    successfullyUpdated: i18n(
      "modules.editSourceName.successfullyUpdated",
    ),
  };

  const logger = getLogger();
  const [showInput, setShowInput] = useState(false);
  const [newSourceName, setNewSourceName] = useState(source.name);

  const updateNewSourceName = (e, value: string) => {
    e.stopPropagation();
    logger.debug("Updating name:", value);
    setNewSourceName(value);
  };

  const editSourceName = (e) => {
    e.stopPropagation();
    logger.debug("editSourceName");
    setShowInput(true);
  };

  const onKeyDownFunc = (e) => {
    e.stopPropagation();
    logger.debug("onKeyDownFunc");
    if (e.key === "Enter") {
      e.preventDefault();
      saveSourceName(e);
    }
  };

  const updateItemNameInReduxState = (newSourceNameToBeSaved: string) => {
    logger.debug("updateItemNameInReduxState", newSourceNameToBeSaved);

    const clonedData = structuredClone(allMachinesListData);
    let didUpdate = false;

    if (clonedData) {
      Object.keys(clonedData).forEach((gatewayKey) => {
        const gateway = clonedData[gatewayKey] as transformedGatewaysWithMachinesListEntryTypes;
        if (gateway?.sources) {
          const updated = updateSourceNameInMachinesList(
            gateway.sources,
            source.source_id,
            newSourceNameToBeSaved,
          );
          if (updated) didUpdate = true;
        }
      });
    }

    if (didUpdate) {
      dispatch(updateAllMachinesListData(clonedData));
    }
  };

  const updateSourceNameInApi = async (newSourceNameToBeSaved: string) => {
    logger.debug("updateSourceNameInApi", newSourceNameToBeSaved);

    const response = await updateSourceName({
      name: newSourceNameToBeSaved,
      source_id: source.source_id,
      source_type: source.source_type,
    });

    return response;
  };

  const saveSourceName = async (e) => {
    e.stopPropagation();
    logger.debug("saveSourceName", newSourceName);
    const newSourceNameToBeSaved = newSourceName.trim();
    setNewSourceName(newSourceNameToBeSaved);

    if (!newSourceName) {
      errorSnackbar(texts.errorInputEmpty);
      return;
    }

    setShowInput(false);

    const response = await updateSourceNameInApi(newSourceNameToBeSaved);

    if (response && response.success) {
      successSnackbar(texts.successfullyUpdated, 5000);
      updateItemNameInReduxState(newSourceNameToBeSaved);
    } else {
      errorSnackbar(texts.errorUpdateFailed, 5000);
      setNewSourceName(previousName);
    }
  };

  const cancelSaveSourceName = (e: ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    logger.debug("CancelSaveSourceName", newSourceName);
    setNewSourceName(source.name);
    setShowInput(false);
  };

  useEffect(() => {
    logger.debug("EditSourceName mounted");
    logger.debug("EditSourceName item", source);

    setNewSourceName(source.name);
    return () => {
      logger.debug("EditSourceName unmounted");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    showInput: showInput,
    editSourceName: editSourceName,
    onKeyDownFunc: onKeyDownFunc,
    updateNewSourceName: updateNewSourceName,
    saveSourceName: saveSourceName,
    cancelSaveSourceName: cancelSaveSourceName,
    newSourceName: newSourceName,
  };
};
